import type { ApiClient } from "@carepass/api-client";
import type { Schemas } from "../workspace/api.generated";
import type { InquirySearchFormValues } from "./schema";

export interface InquiryListQuery {
  readonly status: InquirySearchFormValues["status"];
  readonly memberId?: number;
  readonly assignedAdminLoginId?: string;
  readonly from: string;
  readonly to: string;
  readonly page: number;
  readonly size: number;
}

export async function getInquiries(
  apiClient: ApiClient,
  query: InquiryListQuery,
  signal?: AbortSignal,
): Promise<Schemas["AdminPageInquiryView"]> {
  const response = await apiClient.get<Schemas["AdminPageInquiryView"]>(
    "/api/v1/admin/inquiries/search",
    {
      query: {
        status: query.status || undefined,
        memberId: query.memberId,
        assignedAdminLoginId: query.assignedAdminLoginId,
        from: query.from || undefined,
        to: query.to || undefined,
        page: query.page,
        size: query.size,
      },
      signal,
      cache: "no-store",
    },
  );
  return response.data;
}

export async function assignInquiry(
  apiClient: ApiClient,
  inquiryId: number,
  signal?: AbortSignal,
): Promise<Schemas["InquiryView"]> {
  const response = await apiClient.post<Schemas["InquiryView"]>(
    `/api/v1/admin/inquiries/${inquiryId}/assignment`,
    undefined,
    { signal, cache: "no-store" },
  );
  return response.data;
}

export async function getInquiry(
  apiClient: ApiClient,
  inquiryId: number,
  signal?: AbortSignal,
): Promise<Schemas["InquiryView"]> {
  const response = await apiClient.get<Schemas["InquiryView"]>(
    `/api/v1/admin/inquiries/${inquiryId}`,
    { signal, cache: "no-store" },
  );
  return response.data;
}

export async function replyInquiry(
  apiClient: ApiClient,
  inquiryId: number,
  input: Schemas["CreateInquiryReplyRequest"],
  signal?: AbortSignal,
): Promise<Schemas["InquiryView"]> {
  const response = await apiClient.post<
    Schemas["InquiryView"],
    Schemas["CreateInquiryReplyRequest"]
  >(`/api/v1/admin/inquiries/${inquiryId}/replies`, input, {
    signal,
    cache: "no-store",
  });
  return response.data;
}

export async function findInquiryForReconciliation(
  apiClient: ApiClient,
  inquiryId: number,
  query: Pick<InquiryListQuery, "memberId" | "from" | "to">,
  signal?: AbortSignal,
): Promise<Schemas["InquiryView"] | undefined> {
  void query;
  return getInquiry(apiClient, inquiryId, signal);
}
